import { z } from "zod";
export const AGENDA_TIMEZONE = "America/Sao_Paulo";
const day = z.iso.date();
export const appointmentRecurrenceSchema = z.object({
    frequency: z.enum(["DAILY", "WEEKLY", "BIWEEKLY", "MONTHLY"]),
    interval: z.number().int().min(1).max(365),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7),
    startDate: day,
    end: z.discriminatedUnion("type", [
        z.object({ type: z.literal("NEVER") }),
        z.object({ type: z.literal("COUNT"), count: z.number().int().min(1).max(10000) }),
        z.object({ type: z.literal("DATE"), date: day })
    ])
}).refine(r => !["WEEKLY", "BIWEEKLY"].includes(r.frequency) || r.weekdays.length > 0, "Selecione ao menos um dia da semana.")
    .refine(r => r.end.type !== "DATE" || r.end.date >= r.startDate, "O término deve ser posterior ao início.");
// Civil dates, not instants: UTC arithmetic avoids host timezone/DST changing the day.
export function addAgendaDays(value, amount) {
    const date = new Date(`${value}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + amount);
    return date.toISOString().slice(0, 10);
}
export function agendaToday(now = new Date()) {
    return new Intl.DateTimeFormat("en-CA", { timeZone: AGENDA_TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}
const holidayCache = new Map();
// Federal calendar (MGI 11.460/2025): includes Good Friday; excludes optional
// Carnival/Corpus Christi and state/municipal holidays. One calendar for all consumers.
export function brazilNationalHolidays(year) {
    const cached = holidayCache.get(year);
    if (cached)
        return cached;
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451), n = h + l - 7 * m + 114;
    const easter = `${year}-${String(Math.floor(n / 31)).padStart(2, "0")}-${String(n % 31 + 1).padStart(2, "0")}`;
    const result = new Set(["01-01", "04-21", "05-01", "09-07", "10-12", "11-02", "11-15", "12-25", ...(year >= 2024 ? ["11-20"] : [])].map(date => `${year}-${date}`));
    result.add(addAgendaDays(easter, -2));
    holidayCache.set(year, result);
    return result;
}
export function holidayAdjustedDate(original, calendar = brazilNationalHolidays) {
    if (!calendar(Number(original.slice(0, 4))).has(original))
        return original;
    let actual = original;
    do {
        actual = addAgendaDays(actual, 1);
    } while ([0, 6].includes(new Date(`${actual}T12:00:00Z`).getUTCDay()) || calendar(Number(actual.slice(0, 4))).has(actual));
    return actual;
}
export function recurrenceDates(rule, from, through) {
    const result = [];
    const start = new Date(`${rule.startDate}T12:00:00Z`);
    const weekday = start.getUTCDay();
    const mondayOffset = (weekday + 6) % 7;
    let count = 0;
    // Stops at the requested window; an endless series is never expanded infinitely.
    for (let current = rule.startDate; current <= through; current = addAgendaDays(current, 1)) {
        if (rule.end.type === "DATE" && current > rule.end.date)
            break;
        const civil = new Date(`${current}T12:00:00Z`);
        const elapsed = Math.round((civil.getTime() - start.getTime()) / 86400000);
        const months = (civil.getUTCFullYear() - start.getUTCFullYear()) * 12 + civil.getUTCMonth() - start.getUTCMonth();
        const lastDay = new Date(Date.UTC(civil.getUTCFullYear(), civil.getUTCMonth() + 1, 0)).getUTCDate();
        const matches = rule.frequency === "DAILY" ? elapsed % rule.interval === 0
            : rule.frequency === "MONTHLY" ? months % rule.interval === 0 && civil.getUTCDate() === Math.min(start.getUTCDate(), lastDay)
                : Math.floor((elapsed + mondayOffset) / 7) % (rule.interval * (rule.frequency === "BIWEEKLY" ? 2 : 1)) === 0 && rule.weekdays.includes(civil.getUTCDay());
        if (!matches)
            continue;
        count++;
        if (rule.end.type === "COUNT" && count > rule.end.count)
            break;
        const date = holidayAdjustedDate(current);
        if (date >= from && date <= through)
            result.push({ originalDate: current, date, shiftReason: date !== current ? "HOLIDAY" : null });
    }
    return result;
}
